import React, { useState } from 'react';
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { identityConflicts, identityFields, linkedPerson, linkSignatory, personIdentity, personSuggestionKey, sameNamePeople } from '../../../utils/linkedScreeningPerson';
import '../css/SignatoryPersonLink.css';

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
function conflictValueLabel(field, value) {
  if (field === 'nationality' || field === 'countryOfResidence') {
    try { return countryNames.of(String(value).trim().toUpperCase()) || value; } catch { return value; }
  }
  return value;
}

function PersonSuggestion({ person, row, onConfirm, onDifferent }) {
  const isPresent = useIsPresent();
  const [confirmingUpdate, setConfirmingUpdate] = useState(false);
  const conflicts = identityConflicts(row, person.row);
  const source = personIdentity(person.row), signatory = personIdentity(row);
  return <div inert={!isPresent} className={`signatoryPersonLink signatoryPersonLink--suggestion${confirmingUpdate ? ' signatoryPersonLink--conflicts' : ''}`}>
    <div className="signatoryPersonLinkIdentity">
    <div className="fw-semibold signatoryPersonLinkHeading">An existing {person.collection === 'shareholders' ? 'shareholder' : 'controlling person'} has the same name</div>
    <div><strong>{source.name}</strong> · {person.role}</div>
    {(source.dateOfBirth || source.nationality) && <div className="text-muted">{[source.dateOfBirth && `Date of birth: ${source.dateOfBirth}`, source.nationality && `Nationality: ${conflictValueLabel('nationality', source.nationality)}`].filter(Boolean).join(' · ')}</div>}
    </div>
    <div className="signatoryPersonLinkDecision">
      <div className="fw-semibold">Is this the same person?</div>
      {confirmingUpdate ? <div className="mt-2" role="status">Same person</div> : <div className="signatoryPersonLinkActions">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => conflicts.length ? setConfirmingUpdate(true) : onConfirm({})}>Yes, use this person</button>
        <button type="button" className="btn btn-sm btn-link text-muted" onClick={onDifferent}>No, different person</button>
      </div>}
    </div>
    {confirmingUpdate && <div className="signatoryPersonLinkConflicts" role="status">
      <div className="fw-bold">Replace the following signatory details with the {person.collection === 'shareholders' ? 'shareholder’s' : 'controlling person’s'} details:</div>
      <ul className="signatoryPersonLinkChanges">
        {conflicts.map(field => <li key={field}>
          {identityFields[field]}: {conflictValueLabel(field, signatory[field])}
          {' → '}<strong>{conflictValueLabel(field, source[field])}</strong>
        </li>)}
      </ul>
      <div className="signatoryPersonLinkActions">
        <button type="button" className="btn btn-sm btn-link text-muted" onClick={() => setConfirmingUpdate(false)}><i className="bi bi-arrow-left me-1" aria-hidden="true" />Back</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onConfirm(Object.fromEntries(conflicts.map(field => [field, 'source'])))}>Confirm update and link</button>
      </div>
    </div>}
  </div>;
}

export default function SignatoryPersonLink({ ownership, index, onChange, readOnly }) {
  const reduceMotion = useReducedMotion();
  const row = ownership.signatories[index];
  const linked = linkedPerson(ownership, row);
  const unlink = () => onChange({ ...ownership, signatories: ownership.signatories.map((item, i) => i === index ? { ...item, _personLink: null } : item) });
  if (readOnly) return null;
  return <>
    {row?._personLink && !linked && <div className="signatoryPersonLink">The linked person is no longer in Ownership. This signatory is kept separate. <button type="button" className="btn btn-sm btn-link" onClick={unlink}>Remove link</button></div>}
    <AnimatePresence initial={false}>
      {sameNamePeople(ownership, row).map(person => <motion.div
        key={`${personSuggestionKey(person)}|${JSON.stringify(personIdentity(row))}`}
        initial={false}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0, pointerEvents: 'none' }}
        transition={{ duration: reduceMotion ? 0 : 0.25 }}
        style={{ overflow: 'hidden' }}
      >
        <PersonSuggestion person={person} row={row}
          onConfirm={choices => onChange(linkSignatory(ownership, index, person, choices))}
          onDifferent={() => onChange({ ...ownership, signatories: ownership.signatories.map((item, i) => i === index ? { ...item, _differentPeople: [...(item._differentPeople || []), personSuggestionKey(person)] } : item) })} />
      </motion.div>)}
    </AnimatePresence>
  </>;
}
